import React, { useEffect, useRef, useState } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box } from '@mui/material';
import SelectField from '../../Utils/SelectField';
import InputField from '../../Utils/InputField';
import CommonDateTimePicker from '../../Utils/CommonDateTimePicker';
import moment from 'moment';
import { CycleAlignmentOption, recurringFrequencyOptions, validityEndOptions, validityStartOptions, validityTimeOptions } from './constants/initialValues';
import PricingModelTableRecurring from './PricingModelTableRecurring';
import { Col } from 'reactstrap';
import { isEmptyOrInvalidBalanceImpactRecurringField } from './constants/checkEmptyFields';


function BalanceImpactTableRecurring({ isSubscribed, resourceNameOptions, uomOptions, pricingModelOptions, tableDataRecurring, setTableDataRecurring, index, values, setFieldTouched, setTouched, setFieldValue, errors, touched, isSubmitting }) {
    const [columns, setColumns] = useState([]);
    const [tableKey, setTableKey] = useState(0);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [focusedField, setFocusedField] = useState(null);
    const onChange = (rowIndex, key, value) => {
        const newData = [...tableDataRecurring];
        newData[rowIndex][key] = value;
        setTableDataRecurring(newData);

        if (key === 'pricingModel') {
            if (value.value === 'volume') {
                setExpandedRowId(rowIndex.toString());
            } else if (expandedRowId === rowIndex.toString()) {
                setExpandedRowId(null);
            }
        } else {
            if (expandedRowId !== null && expandedRowId !== rowIndex.toString()) {
                setExpandedRowId(null);
            }
        }
    };
    const onChangePricingModel = (rowIndex, key, modelIndex, value) => {
        const newData = [...tableDataRecurring];
        if (!newData[rowIndex].pricingModelNew) {
            newData[rowIndex].pricingModelNew = [{
                startingUnit: '',
                endingUnit: '',
                pricePerUnit: '',
            }];
        }
        newData[rowIndex].pricingModelNew[modelIndex][key] = value;
        setTableDataRecurring(newData);
    };
    const handleAddRow = () => {
        setTableDataRecurring([
            ...tableDataRecurring,
            {
                resourceName: '',
                uom: '',
                flatRate: '',
                validityStart: '',
                validityEnd: '',
                pricingModel: '',
                pricingModelNew: [
                    {
                        startingUnit: '',
                        endingUnit: '',
                        pricePerUnit: '',
                    }
                ],
                perUnitRate: '',
                startDateTime: null,
                timeUnit: '',
                endDateTime: null,
                perUnitRate: '',
                recurringFrequency: '',
                recurringFrequencyUnit: 1,
                cycleAlignment: CycleAlignmentOption[1],
            },
        ]);
        setTouched(prev => ({
            ...prev,
            recurringUsageRatePlan: prev.recurringUsageRatePlan?.map((plan, idx) => {
                if (idx === index) {
                    return {
                        ...plan,
                        balanceImpacts: [
                            ...(plan.balanceImpacts || []),
                            {
                                resourceName: false,
                                flatRate: false,
                                validityStart: false,
                                validityEnd: false,
                                pricingModel: false,
                                pricingModelNew: [{
                                    startingUnit: false,
                                    endingUnit: false,
                                    pricePerUnit: false,
                                }],
                                perUnitRate: false,
                                startDateTime: false,
                                timeUnit: false,
                                endDateTime: false,
                                timeValue: false,
                                recurringFrequency: false,
                                cycleAlignment: false,
                            }
                        ]
                    };
                }
                return plan;
            })
        }));
    };

    const handleDeleteRow = (index) => {
        const newData = [...tableDataRecurring];
        newData.splice(index, 1);
        setTableDataRecurring(newData);
    };

    useEffect(() => {
        const hasStartDateTime = tableDataRecurring?.some(row => row.validityStart.value === 3);
        const isEndRelative = tableDataRecurring?.some(row => row.validityEnd.value === 3);
        const isEndSpecific = tableDataRecurring?.some(row => row.validityEnd.value === 2);
        const isFixed = tableDataRecurring?.some(row => row.pricingModel.value === "threshold");
        const getColumns = () => {
            const baseCols = [
                {
                    accessorKey: 'resourceName',
                    header: 'Resource Name',
                    Cell: ({ row, table }) => {
                        const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.resourceName;
                        const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.resourceName;
                        const selectRef = useRef(null);
                        const { focusedField, setFocusedField } = table.options.tableInstanceRef;
                        const fieldName = `balanceImpacts[${row.index}].resourceName`;

                        useEffect(() => {
                            if (focusedField === fieldName && selectRef.current) {
                                selectRef.current.focus?.();
                            }
                        }, [focusedField]);

                        return (
                            <div className="position-relative">
                                <SelectField
                                    innerRef={selectRef}
                                    onFocus={() => {
                                        setExpandedRowId(null)
                                        setFocusedField(fieldName)
                                    }}
                                    isDisabled={isSubscribed}
                                    className={`${isSubscribed ? "label-disable-color-edit-offer" : ""} w-100`}
                                    menuPortalTarget={document.querySelector('body')}
                                    value={row.original.resourceName ? resourceNameOptions.find(option => option.value === row.original.resourceName.value) : null}
                                    handleChange={(selectedOption) =>
                                        onChange(row.index, 'resourceName', selectedOption)
                                    }
                                    options={resourceNameOptions}
                                />
                                {touchedField && error && (
                                    <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                                )}
                            </div>
                        );
                    }
                },
                {
                    accessorKey: 'uom',
                    header: 'Uom',
                    Cell: ({ row, table }) => {
                        const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.uom;
                        const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.uom;
                        const selectRef = useRef(null);
                        const { focusedField, setFocusedField } = table.options.tableInstanceRef;
                        const fieldName = `balanceImpacts[${row.index}].uom`;

                        useEffect(() => {
                            if (focusedField === fieldName && selectRef.current) {
                                selectRef.current.focus?.();
                            }
                        }, [focusedField]);

                        return (
                            <div className="position-relative">
                                <SelectField
                                    innerRef={selectRef}
                                    onFocus={() => {
                                        setExpandedRowId(null)
                                        setFocusedField(fieldName)
                                    }}
                                    isDisabled={isSubscribed}
                                    className={`${isSubscribed ? "label-disable-color-edit-offer" : ""} w-100`}
                                    menuPortalTarget={document.querySelector('body')}
                                    value={row.original.uom ? resourceNameOptions.find(option => option.value === row.original.uom.value) : null}
                                    handleChange={(selectedOption) =>
                                        onChange(row.index, 'uom', selectedOption)
                                    }
                                    options={uomOptions}
                                />
                                {touchedField && error && (
                                    <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                                )}
                            </div>
                        );
                    }
                },
                {
                    accessorKey: 'flatRate',
                    header: 'Flat Rate',
                    Cell: ({ row, table }) => {
                        const inputRef = useRef(null);
                        const { focusedField, setFocusedField } = table.options.tableInstanceRef;
                        const fieldName = `balanceImpacts[${row.index}].flatRate`;
                        const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.flatRate;
                        const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.flatRate;

                        useEffect(() => {
                            if (focusedField === fieldName && inputRef.current) {
                                inputRef.current.focus();
                                const val = inputRef.current.value;
                                inputRef.current.setSelectionRange?.(val.length, val.length);
                            }
                        }, [focusedField]);

                        return (
                            <div className="position-relative">
                                <InputField
                                    name={fieldName}
                                    innerRef={inputRef}
                                    onFocus={() => {
                                        setFocusedField(fieldName)
                                        setExpandedRowId(null)
                                    }}
                                    isDisabled={isSubscribed}
                                    placeholder="Enter Flat Rate"
                                    type="string"
                                    className={`${touchedField && error ? "is-invalid" : ""}`}
                                    onKeyDown={(event) => {
                                        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', '.', 'Tab'];
                                        if (!/[0-9]/.test(event.key) && !allowedKeys.includes(event.key)) {
                                            event.preventDefault();
                                        }
                                    }}
                                    handleChange={(e) => onChange(row.index, 'flatRate', e.target.value)}
                                    value={row.original.flatRate}
                                />
                                {touchedField && error && (
                                    <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                                )}
                            </div>
                        );
                    }
                },
                {
                    accessorKey: 'recurringFrequency',
                    header: 'Recurring Frequency',
                    Cell: ({ row, table }) => {
                        const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.recurringFrequency;
                        const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.recurringFrequency;
                        const selectRef = useRef(null);
                        const { focusedField, setFocusedField } = table.options.tableInstanceRef;
                        const fieldName = `balanceImpacts[${row.index}].recurringFrequency`;

                        useEffect(() => {
                            if (focusedField === fieldName && selectRef.current) {
                                selectRef.current.focus?.();
                            }
                        }, [focusedField]);
                        return (
                            <div className="position-relative">
                                <SelectField
                                    innerRef={selectRef}
                                    onFocus={() => {
                                        setExpandedRowId(null)
                                        setFocusedField(fieldName)
                                    }}
                                    menuPortalTarget={document.querySelector('body')}
                                    isDisabled={isSubscribed}
                                    value={row.original.recurringFrequency ? recurringFrequencyOptions.find(option => option === row?.original?.recurringFrequency) : null}
                                    handleChange={(selectedOption) => {
                                        onChange(row.index, 'recurringFrequency', selectedOption)
                                    }}
                                    options={recurringFrequencyOptions}
                                />
                                {touchedField && error && (
                                    <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                                )}
                            </div>
                        );
                    },
                },
                {
                    accessorKey: 'recurringFrequencyUnit',
                    header: 'Recurring Frequency Unit',
                    Cell: ({ row, table }) => {
                        const inputRef = useRef(null);
                        const { focusedField, setFocusedField } = table.options.tableInstanceRef;
                        const fieldName = `balanceImpacts[${row.index}].recurringFrequencyUnit`;
                        const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.recurringFrequencyUnit;
                        const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.recurringFrequencyUnit;

                        useEffect(() => {
                            if (focusedField === fieldName && inputRef.current) {
                                inputRef.current.focus();
                                const val = inputRef.current.value;
                                inputRef.current.setSelectionRange?.(val.length, val.length);
                            }
                        }, [focusedField]);

                        return (
                            <div className="position-relative">
                                <InputField
                                    name={fieldName}
                                    innerRef={inputRef}
                                    onFocus={() => {
                                        setFocusedField(fieldName)
                                        setExpandedRowId(null)
                                    }}
                                    isDisabled={isSubscribed}
                                    placeholder="Enter Recurring Frequency Unit"
                                    type="string"
                                    className={`${touchedField && error ? "is-invalid" : ""}`}
                                    handleChange={(e) => onChange(row.index, 'recurringFrequencyUnit', e.target.value)}
                                    value={row.original.recurringFrequencyUnit}
                                />
                                {touchedField && error && (
                                    <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                                )}
                            </div>
                        );
                    }
                },
                {
                    accessorKey: 'cycleAlignment',
                    header: 'Cycle Alignment',
                    Cell: ({ row, table }) => {
                        const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.cycleAlignment;
                        const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.cycleAlignment;
                        const selectRef = useRef(null);
                        const { focusedField, setFocusedField } = table.options.tableInstanceRef;
                        const fieldName = `balanceImpacts[${row.index}].cycleAlignment`;

                        useEffect(() => {
                            if (focusedField === fieldName && selectRef.current) {
                                selectRef.current.focus?.();
                            }
                        }, [focusedField]);

                        return (
                            <div className="position-relative">
                                <SelectField
                                    innerRef={selectRef}
                                    onFocus={() => {
                                        setFocusedField(fieldName)
                                        setExpandedRowId(null)
                                    }}
                                    menuPortalTarget={document.querySelector('body')}
                                    isDisabled={isSubscribed}
                                    value={row.original.cycleAlignment ? CycleAlignmentOption.find(option => option === row?.original?.cycleAlignment) : null}
                                    handleChange={(selectedOption) => {
                                        onChange(row.index, 'cycleAlignment', selectedOption)
                                    }}
                                    options={CycleAlignmentOption}
                                />
                                {touchedField && error && (
                                    <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                                )}
                            </div>
                        )
                    },
                },
                {
                    accessorKey: 'validityStart',
                    header: 'Validity Starts',
                    Cell: ({ row, table }) => {
                        const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.validityStart;
                        const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.validityStart;
                        const selectRef = useRef(null);
                        const { focusedField, setFocusedField } = table.options.tableInstanceRef;
                        const fieldName = `balanceImpacts[${row.index}].validityStart`;

                        useEffect(() => {
                            if (focusedField === fieldName && selectRef.current) {
                                selectRef.current.focus?.();
                            }
                        }, [focusedField]);

                        return (
                            <div className="position-relative" >
                                <SelectField
                                    innerRef={selectRef}
                                    onFocus={() => {
                                        setFocusedField(fieldName)
                                        setExpandedRowId(null)
                                    }}
                                    menuPortalTarget={document.querySelector('body')}
                                    isDisabled={isSubscribed}
                                    value={row.original.validityStart ? validityStartOptions.find(option => option === row.original.validityStart) : null}
                                    options={validityStartOptions}
                                    handleChange={(selectedOption) => onChange(row.index, 'validityStart', selectedOption)}
                                />
                                {touchedField && error && (
                                    <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                                )}
                            </div>
                        );
                    },
                }
            ];
            const startDateCol = hasStartDateTime
                ? [{
                    accessorKey: 'startDateTime',
                    header: 'Start Date and Time',
                    Cell: ({ row }) => {
                        const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.startDateTime;
                        const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.startDateTime;
                        const isActive = row.original.validityStart.value === 3;
                        return (
                            <div className="position-relative">
                                <CommonDateTimePicker
                                    type="datetime-local"
                                    disabled={isSubscribed || !isActive}
                                    minDate={moment()}
                                    handleChange={(newValue) => onChange(row.index, 'startDateTime', newValue)}
                                    value={row.original.startDateTime}
                                />
                                {touchedField && error && (
                                    <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                                )}
                            </div>
                        );
                    },
                }]
                : [];
            const validityEndCol = [
                {
                    accessorKey: 'validityEnd',
                    header: 'Validity Ends',
                    Cell: ({ row, table }) => {
                        const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.validityEnd;
                        const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.validityEnd;
                        const selectRef = useRef(null);
                        const { focusedField, setFocusedField } = table.options.tableInstanceRef;
                        const fieldName = `balanceImpacts[${row.index}].validityEnd`;

                        useEffect(() => {
                            if (focusedField === fieldName && selectRef.current) {
                                selectRef.current.focus?.();
                            }
                        }, [focusedField]);

                        return (
                            <div className="position-relative" >
                                <SelectField
                                    innerRef={selectRef}
                                    onFocus={() => {
                                        setFocusedField(fieldName)
                                        setExpandedRowId(null)
                                    }}
                                    menuPortalTarget={document.querySelector('body')}
                                    isDisabled={isSubscribed}
                                    value={row.original.validityEnd ? validityEndOptions.find(option => option === row.original.validityEnd) : null}
                                    options={validityEndOptions}
                                    handleChange={(selectedOption) => onChange(row.index, 'validityEnd', selectedOption)}
                                />
                                {touchedField && error && (
                                    <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                                )}
                            </div>
                        )
                    },
                },
            ]
            const endRelativeCol = isEndRelative
                ? [
                    {
                        accessorKey: 'timeUnit',
                        header: 'Time Unit',
                        Cell: ({ row, table }) => {
                            const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.timeUnit;
                            const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.timeUnit;
                            const isActive = row.original.validityEnd.value === 3;
                            const selectRef = useRef(null);
                            const { focusedField, setFocusedField } = table.options.tableInstanceRef;
                            const fieldName = `balanceImpacts[${row.index}].validityEnd`;

                            useEffect(() => {
                                if (focusedField === fieldName && selectRef.current) {
                                    selectRef.current.focus?.();
                                }
                            }, [focusedField]);

                            return (
                                <div className="position-relative" >
                                    <SelectField
                                        innerRef={selectRef}
                                        onFocus={() => {
                                            setFocusedField(fieldName)
                                            setExpandedRowId(null)
                                        }}
                                        menuPortalTarget={document.querySelector('body')}
                                        isDisabled={isSubscribed || !isActive}
                                        options={validityTimeOptions}
                                        handleChange={(selectedOption) => onChange(row.index, 'timeUnit', selectedOption)}
                                        value={row.original.timeUnit ? validityTimeOptions.find(option => option === row.original.timeUnit) : null}
                                    />
                                    {touchedField && error && (
                                        <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                                    )}
                                </div>
                            );
                        },
                    },
                    {
                        accessorKey: 'timeValue',
                        header: 'Time Value',
                        Cell: ({ row, table }) => {
                            const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.timeValue;
                            const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.timeValue;
                            const isActive = row.original.validityEnd.value === 3;
                            const inputRef = useRef(null);
                            const { focusedField, setFocusedField } = table.options.tableInstanceRef;
                            const fieldName = `balanceImpacts[${row.index}].timeValue`;


                            useEffect(() => {
                                if (focusedField === fieldName && inputRef.current) {
                                    inputRef.current.focus();
                                    const val = inputRef.current.value;
                                    inputRef.current.setSelectionRange?.(val.length, val.length);
                                }
                            }, [focusedField]);

                            return (
                                <div className="position-relative" >
                                    <InputField
                                        name={fieldName}
                                        innerRef={inputRef}
                                        onFocus={() => {
                                            setFocusedField(fieldName)
                                            setExpandedRowId(null)
                                        }}
                                        disabled={isSubscribed || !isActive}
                                        placeholder="Enter Time Value"
                                        type="text"
                                        onKeyDown={(event) => {
                                            const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', '.', 'Tab'];
                                            if (!/[0-9]/.test(event.key) && !allowedKeys.includes(event.key)) {
                                                event.preventDefault();
                                            }
                                        }}
                                        handleChange={(e) => onChange(row.index, 'timeValue', e.target.value)}
                                        value={row.original.timeValue}
                                    />
                                    {touchedField && error && (
                                        <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                                    )}
                                </div>
                            );
                        },
                    },
                ]
                : [];
            const endSpecificCol = isEndSpecific ? [{
                accessorKey: 'endDateTime',
                header: 'End Date and Time',
                Cell: ({ row }) => {
                    const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.endDateTime;
                    const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.endDateTime;
                    const isActive = row.original.validityEnd.value === 2;
                    return (
                        <div className="position-relative" >
                            <CommonDateTimePicker
                                type="datetime-local"
                                disabled={isSubscribed || !isActive}
                                minDate={moment()}
                                handleChange={(newValue) => onChange(row.index, 'endDateTime', newValue)}
                                value={row.original.endDateTime}
                            />
                            {touchedField && error && (
                                <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                            )}
                        </div>
                    );
                },
            }]
                : [];
            const pricingModelCol = [
                {
                    accessorKey: 'pricingModel',
                    header: 'Pricing Model',
                    Cell: ({ row, table }) => {
                        const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.pricingModel;
                        const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.pricingModel;

                        return (
                            <div className="position-relative" >
                                <SelectField
                                    extraBgColor={row.original.pricingModel?.value === "volume" ? "#eff2f7" : undefined}
                                    menuPortalTarget={document.querySelector('body')}
                                    isDisabled={isSubscribed}
                                    value={row.original.pricingModel ? pricingModelOptions.find(option => option === row.original.pricingModel) : null}
                                    options={pricingModelOptions}
                                    handleChange={(selectedOption) => onChange(row.index, 'pricingModel', selectedOption)}
                                />
                                {touchedField && error && (
                                    <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                                )}
                            </div>
                        )
                    },
                }
            ]
            const pricingModelSupportCol = isFixed
                ? [
                    {
                        accessorKey: 'perUnitRate',
                        header: 'Per Unit Rate',
                        Cell: ({ row, table }) => {
                            const error = errors?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.pricingModel;
                            const touchedField = touched?.recurringUsageRatePlan?.[index]?.balanceImpacts?.[row.index]?.pricingModel;
                            const isActive = (row.original.pricingModel?.value === 'threshold');
                            const inputRef = useRef(null);
                            const { focusedField, setFocusedField } = table.options.tableInstanceRef;
                            const fieldName = `balanceImpacts[${row.index}].perUnitRate`;

                            useEffect(() => {
                                if (focusedField === fieldName && inputRef.current) {
                                    inputRef.current.focus();
                                    const val = inputRef.current.value;
                                    inputRef.current.setSelectionRange?.(val.length, val.length);
                                }
                            }, [focusedField]);

                            return (
                                <div className="position-relative" >
                                    <InputField
                                        name={fieldName}
                                        innerRef={inputRef}
                                        onFocus={() => {
                                            setExpandedRowId(null)
                                            setFocusedField(fieldName)
                                        }}
                                        disabled={isSubscribed || !isActive}
                                        placeholder="Enter Per Unit Rate"
                                        type="text"
                                        onKeyDown={(event) => {
                                            const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', '.', 'Tab'];
                                            if (!/[0-9]/.test(event.key) && !allowedKeys.includes(event.key)) {
                                                event.preventDefault();
                                            }
                                        }}
                                        handleChange={(e) => onChange(row?.index, 'perUnitRate', e.target.value)}
                                        value={row?.original?.perUnitRate}
                                    />
                                    {touchedField && error && (
                                        <p className="text-danger position-absolute ms-1" style={{ fontSize: '0.75rem', top: '100%', left: 0 }}>{error}</p>
                                    )}
                                </div>
                            );
                        },
                    },
                ]
                : [];
            const remainingCols = !isSubscribed ? [
                {
                    id: 'actions',
                    header: 'Action',
                    size: 80,
                    enableColumnOrdering: false,
                    Cell: ({ row }) =>
                        <div className='ms-2'>
                            {row?.index === 0 ? (
                                <i
                                    className="ri-add-circle-line text-primary fs-3"
                                    onClick={() => {
                                        const lastIndex = values?.recurringUsageRatePlan[index]?.balanceImpacts.length - 1;
                                        if (lastIndex === -1 || !isEmptyOrInvalidBalanceImpactRecurringField(index, values, lastIndex)) {
                                            handleAddRow();
                                        }
                                    }}
                                />
                            ) : (
                                <i
                                    className="ri-delete-bin-5-line text-danger fs-3"
                                    onClick={() => handleDeleteRow(row.index)}
                                />
                            )}
                        </div>
                }
            ] : [];
            return [...baseCols, ...startDateCol, ...validityEndCol, ...endRelativeCol, ...endSpecificCol, ...pricingModelCol, ...pricingModelSupportCol, ...remainingCols];
        };
        setColumns(getColumns());
        setTableKey(prev => prev + 1);
    }, [JSON.stringify(tableDataRecurring?.map(row => ({
        validityStart: row?.validityStart,
        validityEnd: row?.validityEnd,
        pricingModel: row?.pricingModel,
        pricingModel: row?.pricingModel?.value,
    }))), isSubscribed, isSubmitting, errors]);
    return (
        <Col lg={12}>
            <MaterialReactTable
                tableInstanceRef={{ focusedField, setFocusedField }}
                key={tableKey}
                columns={columns}
                enableExpandAll={false}
                data={tableDataRecurring}
                enableSorting={false}
                enableColumnActions={true}
                enableTopToolbar={false}
                enableBottomToolbar={false}
                enableDensityToggle={false}
                enablePagination={false}
                muiTablePaperProps={{ elevation: 0 }}
                muiTableBodyCellProps={{ sx: { fontFamily: "Inter,sans-serif", border: "none" } }}
                muiTableHeadCellProps={{ sx: { fontFamily: "Inter,sans-serif", fontWeight: 500 } }}
                state={{ expanded: expandedRowId ? { [expandedRowId]: true } : {} }}
                muiExpandButtonProps={({ row }) => ({
                    onClick: () => {
                        const isExpanded = expandedRowId === row.id;
                        setExpandedRowId(isExpanded ? null : row.id);
                    }
                })}
                positionExpandColumn='last'
                renderDetailPanel={({ row }) =>
                    row?.original?.pricingModel?.value === 'volume' ? (
                        <Box
                            sx={{
                                width: '1150px',
                            }}
                        >
                            <PricingModelTableRecurring
                                pricingModelNew={row?.original?.pricingModelNew || []}
                                onChangePricingModel={(key, idx, value) => onChangePricingModel(row.index, key, idx, value)}
                                onAddRow={() => {
                                    const newRow = [...tableDataRecurring];
                                    newRow[row.index].pricingModelNew = [
                                        ...(newRow[row?.index]?.pricingModelNew || []),
                                        { startingUnit: '', endingUnit: '', pricePerUnit: '' }
                                    ];
                                    setTableDataRecurring(newRow);
                                }}
                                onDeleteRow={(modelIndex) => {
                                    const newRow = [...tableDataRecurring];
                                    newRow[row.index]?.pricingModelNew?.splice(modelIndex, 1);
                                    setTableDataRecurring(newRow);
                                }}
                                isSubscribed={isSubscribed}
                            />

                        </Box>
                    ) : null
                }
            />
        </Col>
    );
}
export default BalanceImpactTableRecurring;
